import {Avatar, AvatarFallback} from '@/components/ui/avatar';
import {Badge} from '@/components/ui/badge';
import {Card, CardContent} from '@/components/ui/card';
import {Separator} from '@/components/ui/separator';
import {Tabs, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {cn} from '@/lib/utils';
import {useQuery} from '@tanstack/react-query';
import {ArrowUp, Clock, Info, Target, TrendingUp, Trophy} from 'lucide-react';
import {useMemo, useState} from 'react';
import AthenaAPI from '../APIs/AthenaAPI';
import {User} from '../Auth/User';
import type {AgentData, MetricType, TimePeriodType} from './data';
import {formatPremium, METRIC_KEYS, TIME_PERIOD_KEYS, toAgentData, useRelativeTime} from './data';

/* ─── Podium Styles ─── */

const PODIUM_STYLES: Record<
    number,
    {
        medalBg: string;
        medalText: string;
        medalBorder: string;
        cardBorder: string;
        cardBg: string;
        premiumColor: string;
        glowColor: string;
    }
> = {
    1: {
        medalBg: 'bg-gradient-to-br from-amber-400 to-amber-600',
        medalText: 'text-white',
        medalBorder: 'border-amber-500',
        cardBorder: 'border-amber-300/80',
        cardBg: 'bg-gradient-to-b from-amber-50/90 to-amber-100/60',
        premiumColor: 'text-amber-800',
        glowColor: 'shadow-amber-200/60',
    },
    2: {
        medalBg: 'bg-gradient-to-br from-slate-400 to-slate-600',
        medalText: 'text-white',
        medalBorder: 'border-slate-500',
        cardBorder: 'border-slate-300',
        cardBg: 'bg-gradient-to-b from-slate-50/80 to-slate-100/60',
        premiumColor: 'text-slate-700',
        glowColor: 'shadow-slate-200/40',
    },
    3: {
        medalBg: 'bg-gradient-to-br from-amber-600 to-amber-800',
        medalText: 'text-amber-100',
        medalBorder: 'border-amber-700',
        cardBorder: 'border-amber-200/60',
        cardBg: 'bg-gradient-to-b from-orange-50/60 to-amber-50/40',
        premiumColor: 'text-amber-900',
        glowColor: 'shadow-orange-200/30',
    },
};

/* ─── Deals Pill ─── */

function DealsPill({deals, className}: {deals: number; className?: string}) {
    if (deals <= 0) return null;
    return (
        <span
            className={cn(
                'shrink-0 rounded-full border border-stone-200/60 bg-stone-100/60 px-1.5 py-0.5 text-[10px] text-stone-500',
                className,
            )}
            style={{fontFamily: 'FredokaMedium, sans-serif'}}
        >
            {deals} {deals === 1 ? 'deal' : 'deals'}
        </span>
    );
}

/* ─── Podium Card ─── */

function PodiumCard({
    agent,
    position,
    isCurrentUser,
}: {
    agent: AgentData | null;
    position: number;
    isCurrentUser: boolean;
}) {
    const s = PODIUM_STYLES[position];
    const isFirst = position === 1;

    // Placeholder card for missing agents
    if (!agent) {
        return (
            <Card
                className={cn(
                    'relative flex flex-col items-center gap-0 overflow-hidden border-2 border-dashed border-stone-300/50 py-0',
                    'bg-stone-50/30',
                )}
            >
                <CardContent className="relative z-10 flex w-full flex-col items-center gap-2 px-3 py-5">
                    <div
                        className={cn(
                            'flex shrink-0 items-center justify-center rounded-full border-2 border-dashed border-stone-300/50',
                            isFirst ? 'h-14 w-14' : 'h-11 w-11',
                        )}
                    >
                        <span
                            className={cn('text-stone-300', isFirst ? 'text-2xl' : 'text-lg')}
                            style={{fontFamily: 'FredokaBold, sans-serif'}}
                        >
                            {position}
                        </span>
                    </div>
                    <p className="text-xs text-stone-300" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                        —
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            className={cn(
                'relative flex flex-col items-center gap-0 overflow-hidden border-2 py-0',
                s.cardBorder,
                s.cardBg,
                isFirst ? 'shadow-xl' : 'shadow-md',
                s.glowColor,
                isCurrentUser && 'ring-2 ring-[#456d69] ring-offset-1',
            )}
            style={{
                animationDelay: `${position === 1 ? 0 : position === 2 ? 120 : 240}ms`,
                animationFillMode: 'backwards',
            }}
        >
            {/* Gold shimmer border for #1 */}
            {isFirst && (
                <div
                    className="pointer-events-none absolute -inset-[2px] rounded-xl opacity-60"
                    style={{
                        background:
                            'linear-gradient(105deg, transparent 40%, rgba(251,191,36,0.4) 45%, rgba(252,211,77,0.6) 50%, rgba(251,191,36,0.4) 55%, transparent 60%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 3s ease-in-out infinite',
                    }}
                />
            )}

            <CardContent className="relative z-10 flex w-full flex-col items-center gap-2 px-3 py-5">
                {/* Trophy for #1 */}
                {isFirst && <Trophy className="h-5 w-5 text-amber-500" />}

                {/* Medal */}
                <div
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded-full border-2 drop-shadow-md',
                        isFirst ? 'h-14 w-14' : 'h-11 w-11',
                        s.medalBg,
                        s.medalText,
                        s.medalBorder,
                    )}
                    style={{fontFamily: 'FredokaBold, sans-serif'}}
                >
                    <span className={isFirst ? 'text-2xl' : 'text-lg'}>{agent.rank}</span>
                </div>

                {/* Avatar */}
                <Avatar className={cn(isFirst ? 'h-10 w-10' : 'h-8 w-8', 'shrink-0 ring-2 ring-white')}>
                    <AvatarFallback
                        className={cn('bg-stone-100 text-stone-600', isFirst ? 'text-xs' : 'text-[10px]')}
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        {agent.initials}
                    </AvatarFallback>
                </Avatar>

                {/* Name */}
                <p
                    className={cn('w-full text-center leading-tight text-stone-700', isFirst ? 'text-sm' : 'text-xs')}
                    style={{fontFamily: 'FredokaMedium, sans-serif'}}
                >
                    {agent.name}
                </p>

                {/* "You" badge */}
                {isCurrentUser && (
                    <Badge
                        className="border-[#456d69]/30 bg-[#e8edec] px-2 py-0 text-[10px] text-[#456d69] hover:bg-[#e8edec]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        You
                    </Badge>
                )}

                {/* Deals pill */}
                <DealsPill deals={agent.deals} />

                {/* Premium — the hero number */}
                <p
                    className={cn('text-center tracking-tight', s.premiumColor, isFirst ? 'text-2xl' : 'text-lg')}
                    style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
                >
                    {formatPremium(agent.premium)}
                </p>
            </CardContent>
        </Card>
    );
}

/* ─── Agent Row ─── */

function AgentRow({agent, index, isCurrentUser}: {agent: AgentData; index: number; isCurrentUser: boolean}) {
    const opacity = 1 - index * 0.06;

    return (
        <div
            className={cn(
                'flex items-center gap-3 rounded-lg border border-transparent px-3 py-2',
                isCurrentUser && 'border-[#456d69]/20 bg-[#456d69]/[0.06] ring-1 ring-[#456d69]/20',
            )}
        >
            {/* Left accent bar */}
            <div
                className={cn('h-6 w-[3px] rounded-full', 'bg-[#456d69]')}
                style={!isCurrentUser ? {opacity} : undefined}
            />

            {/* Rank */}
            <Badge
                variant="outline"
                className={cn(
                    'h-7 w-7 shrink-0 items-center justify-center rounded-full px-0',
                    isCurrentUser
                        ? 'border-[1.5px] border-[#456d69]/60 bg-[#e8edec] text-[#456d69]'
                        : 'border-[1.5px] border-[#456d69]/60 bg-[#e8edec]/50 text-[#456d69]',
                )}
                style={{fontFamily: 'FredokaBold, sans-serif', fontSize: '11px'}}
            >
                {agent.rank}
            </Badge>

            {/* Avatar */}
            <Avatar className="h-7 w-7">
                <AvatarFallback
                    className={cn(
                        'border',
                        isCurrentUser
                            ? 'border-[#456d69]/40 bg-[#e8edec] text-[#456d69]'
                            : 'border-stone-200 bg-stone-50 text-stone-500',
                    )}
                    style={{fontFamily: 'FredokaSemiBold, sans-serif', fontSize: '10px'}}
                >
                    {agent.initials}
                </AvatarFallback>
            </Avatar>

            {/* Name + You badge */}
            <div className="flex items-center gap-1.5">
                <span
                    className={cn('text-sm', isCurrentUser ? 'text-[#456d69]' : 'text-stone-700')}
                    style={{fontFamily: isCurrentUser ? 'FredokaSemiBold, sans-serif' : 'FredokaMedium, sans-serif'}}
                >
                    {agent.name}
                </span>
                {isCurrentUser && (
                    <Badge
                        className="shrink-0 border-[#456d69]/30 bg-[#e8edec] px-1.5 py-0 text-[9px] text-[#456d69] hover:bg-[#e8edec]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        You
                    </Badge>
                )}
            </div>

            {/* Deals pill */}
            <DealsPill deals={agent.deals} />

            {/* Dotted leader */}
            <div className="mx-1 flex-1 border-b border-dotted border-stone-300/60" />

            {/* Premium */}
            <span
                className="text-sm text-stone-900"
                style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
            >
                {formatPremium(agent.premium)}
            </span>
        </div>
    );
}

/* ─── Current Agent Row ─── */

function CurrentAgentRow({agent, topAgents}: {agent: AgentData; topAgents: AgentData[]}) {
    const lastInList = topAgents.length > 0 ? topAgents[topAgents.length - 1] : null;
    const gapToList = lastInList ? lastInList.premium - agent.premium : 0;

    return (
        <Card
            className="relative overflow-hidden border-2 border-[#456d69]/40 py-0 shadow-md shadow-[#456d69]/10"
            style={{
                background:
                    'linear-gradient(135deg, rgba(232,237,236,0.9) 0%, rgba(69,109,105,0.08) 50%, rgba(232,237,236,0.7) 100%)',
            }}
        >
            {/* Left accent */}
            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#456d69] to-[#5a8a85]" />

            <CardContent className="flex items-center gap-3 px-4 py-3">
                {/* Rank */}
                <Badge
                    variant="outline"
                    className="h-7 w-7 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#456d69] bg-[#e8edec] px-0 text-[#456d69]"
                    style={{fontFamily: 'FredokaBold, sans-serif', fontSize: '11px'}}
                >
                    {agent.rank}
                </Badge>

                {/* Avatar */}
                <Avatar className="h-7 w-7 ring-2 ring-[#456d69]/20">
                    <AvatarFallback
                        className="border border-[#456d69]/40 bg-[#e8edec] text-[#456d69]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif', fontSize: '10px'}}
                    >
                        {agent.initials}
                    </AvatarFallback>
                </Avatar>

                {/* Name + label */}
                <div className="flex items-center gap-2">
                    <span className="text-sm text-[#456d69]" style={{fontFamily: 'FredokaSemiBold, sans-serif'}}>
                        {agent.name}
                    </span>
                    <Badge
                        className="border-[#456d69]/30 bg-[#e8edec] px-2 py-0 text-[10px] text-[#456d69] hover:bg-[#e8edec]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        You
                    </Badge>
                </div>

                {/* Deals pill */}
                <DealsPill deals={agent.deals} className="border-[#456d69]/20 bg-[#e8edec]/60 text-[#456d69]" />

                {/* Spacer */}
                <div className="flex-1" />

                {/* Gap to top of list */}
                {gapToList > 0 && (
                    <div className="flex items-center gap-1 text-[#456d69]/70">
                        <TrendingUp className="h-3 w-3" />
                        <span className="text-[11px]" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                            {formatPremium(gapToList)} to top {topAgents.length}
                        </span>
                    </div>
                )}

                {/* Separator dot */}
                {gapToList > 0 && <div className="h-1 w-1 rounded-full bg-[#456d69]/30" />}

                {/* Premium */}
                <div className="flex items-center gap-1">
                    <ArrowUp className="h-3.5 w-3.5 text-[#456d69]" />
                    <span
                        className="text-base text-stone-900"
                        style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
                    >
                        {formatPremium(agent.premium)}
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}

/* ─── Skeleton States ─── */

function SkeletonPodium() {
    return (
        <div className="grid w-full max-w-sm grid-cols-3 gap-3">
            {[2, 1, 3].map((pos) => (
                <div key={pos} className={cn(pos !== 1 && 'mt-8', 'self-start')}>
                    <Card className="flex flex-col items-center gap-0 overflow-hidden border-2 border-stone-200/50 py-0">
                        <CardContent className="flex w-full flex-col items-center gap-2 px-3 py-5">
                            <div
                                className={cn(
                                    'animate-pulse rounded-full bg-stone-200',
                                    pos === 1 ? 'h-14 w-14' : 'h-11 w-11',
                                )}
                            />
                            <div
                                className={cn(
                                    'animate-pulse rounded-full bg-stone-200',
                                    pos === 1 ? 'h-10 w-10' : 'h-8 w-8',
                                )}
                            />
                            <div className="h-3 w-20 animate-pulse rounded bg-stone-200" />
                            <div
                                className={cn(
                                    'animate-pulse rounded bg-stone-200',
                                    pos === 1 ? 'h-6 w-24' : 'h-5 w-20',
                                )}
                            />
                        </CardContent>
                    </Card>
                </div>
            ))}
        </div>
    );
}

function SkeletonRows() {
    return (
        <div className="flex flex-col gap-0">
            {Array.from({length: 7}).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2">
                    <div className="h-6 w-[3px] shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-3 w-28 animate-pulse rounded bg-stone-200" />
                    <div className="flex-1" />
                    <div className="h-4 w-16 shrink-0 animate-pulse rounded bg-stone-200" />
                </div>
            ))}
        </div>
    );
}

/* ─── Empty State ─── */

function EmptyState() {
    return (
        <div className="flex flex-1 items-center justify-center">
            <span className="text-sm text-stone-400" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                No activity yet
            </span>
        </div>
    );
}

/* ─── Main Layout ─── */

export default function LeaderboardTab() {
    const [metric, setMetric] = useState<MetricType>('submitted');
    const [timePeriod, setTimePeriod] = useState<TimePeriodType>('daily');

    /* ─── Data fetching (shares cache with popout widget) ─── */

    const {data: leaderboardData, isLoading} = useQuery({
        queryKey: ['leaderboard'],
        queryFn: () => AthenaAPI.getLeaderboard(),
        refetchInterval: 60000,
        refetchOnWindowFocus: false,
    });

    /* ─── Transform API data ─── */

    const {agents, currentAgent} = useMemo(() => {
        if (!leaderboardData?.data) return {agents: [] as AgentData[], currentAgent: null as AgentData | null};

        const periodKey = TIME_PERIOD_KEYS[timePeriod];
        const metricKey = METRIC_KEYS[metric];
        const entries = leaderboardData.data[periodKey]?.[metricKey] ?? [];

        const allAgents = entries.map((entry, i) => toAgentData(entry, i + 1));

        const userName = User.Info.name?.toLowerCase() ?? '';
        const userInList = userName ? allAgents.find((a) => a.name.toLowerCase() === userName) : null;
        const current = userInList && userInList.rank > 10 ? userInList : null;

        return {agents: allAgents, currentAgent: current};
    }, [leaderboardData, timePeriod, metric]);

    const topThree = agents.slice(0, 3);
    const restAgents = agents.slice(3);
    const hasData = agents.length > 0;
    const showCurrentAgent = currentAgent !== null;

    const isCurrentUser = (agent: AgentData) => {
        const userName = User.Info.name?.toLowerCase() ?? '';
        if (!userName || currentAgent) return false;
        return agent.name.toLowerCase() === userName;
    };

    const metricLabel = metric === 'submitted' ? 'New Deal Premium' : 'Delivered Premium';
    const relativeTime = useRelativeTime(leaderboardData?.updatedAt);

    // Pad topThree to always have 3 entries (null for placeholders)
    const podiumAgents: (AgentData | null)[] = [topThree[0] ?? null, topThree[1] ?? null, topThree[2] ?? null];

    return (
        <div className="relative flex h-screen w-full flex-col overflow-hidden bg-gradient-to-br from-[#f8faf9] via-[#f0f4f2] to-stone-100">
            {/* Dot-grid texture overlay */}
            <div
                className="pointer-events-none absolute inset-0 z-0 opacity-[0.35]"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(69,109,105,0.15) 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                }}
            />

            {/* Radial glow behind podium area */}
            <div
                className="pointer-events-none absolute left-0 top-1/4 z-0 h-[500px] w-[500px] -translate-x-1/4 rounded-full"
                style={{
                    background: 'radial-gradient(circle, rgba(69,109,105,0.06) 0%, transparent 70%)',
                }}
            />

            {/* ── Header ── */}
            <header className="relative z-10 flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#456d69] shadow-md shadow-[#456d69]/20">
                        <Target className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <h1
                            className="text-xl tracking-tight text-stone-900"
                            style={{fontFamily: 'LoraSemiBold, serif'}}
                        >
                            Sales Leaderboard
                        </h1>
                        <p
                            className="text-[11px] tracking-wide text-stone-400"
                            style={{
                                fontFamily: 'FredokaMedium, sans-serif',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                            }}
                        >
                            {metricLabel}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Metric toggle */}
                    <Tabs value={metric} onValueChange={(v) => setMetric(v as MetricType)}>
                        <TabsList className="h-8 rounded-lg border border-[#456d69]/20 bg-[#e8edec]/50 backdrop-blur-sm">
                            <TabsTrigger
                                value="submitted"
                                className="rounded-md px-3 py-1 text-xs text-[#456d69]/70 data-[state=active]:bg-[#456d69] data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                New Deal
                            </TabsTrigger>
                            <TabsTrigger
                                value="delivered"
                                className="rounded-md px-3 py-1 text-xs text-[#456d69]/70 data-[state=active]:bg-[#456d69] data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                Delivered
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Divider */}
                    <div className="h-5 w-px bg-stone-300/50" />

                    {/* Time period toggle */}
                    <Tabs value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriodType)}>
                        <TabsList className="h-8 rounded-lg border border-stone-200/80 bg-white/60 backdrop-blur-sm">
                            <TabsTrigger
                                value="daily"
                                className="rounded-md px-3 py-1 text-xs data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                Daily
                            </TabsTrigger>
                            <TabsTrigger
                                value="weekly"
                                className="rounded-md px-3 py-1 text-xs data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                Weekly
                            </TabsTrigger>
                            <TabsTrigger
                                value="monthly"
                                className="rounded-md px-3 py-1 text-xs data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                Monthly
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Updated time + info tooltip */}
                    {leaderboardData?.updatedAt != null && (
                        <div className="flex items-center gap-1.5 text-stone-400">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="text-[11px]" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                                {relativeTime}
                            </span>
                            <div className="group relative">
                                <button
                                    type="button"
                                    className="rounded-full p-0.5 text-stone-400 transition-colors hover:text-stone-600"
                                    aria-label="Data refresh information"
                                    aria-describedby="tab-info-tooltip"
                                >
                                    <Info className="h-3.5 w-3.5" />
                                </button>
                                <div
                                    id="tab-info-tooltip"
                                    role="tooltip"
                                    className="invisible absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-stone-200 bg-white px-3 py-2 text-[11px] leading-relaxed text-stone-600 shadow-md group-hover:visible group-focus-within:visible"
                                    style={{fontFamily: 'FredokaMedium, sans-serif'}}
                                >
                                    Data is refreshed every ~10 minutes, Monday through Friday.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <Separator className="relative z-10 bg-stone-200/60" />

            {/* ── Main Content ── */}
            {/* Empty state — spans both panels */}
            {!isLoading && !hasData && (
                <div className="relative z-10 flex min-h-0 flex-1">
                    <EmptyState />
                </div>
            )}

            {/* Loading state */}
            {isLoading && !hasData && (
                <div className="relative z-10 flex min-h-0 flex-1 gap-0">
                    {/* Left — skeleton podium */}
                    <div className="flex w-[42%] shrink-0 flex-col items-center px-8 pt-6">
                        <div className="mb-4 flex items-center gap-2 self-start">
                            <Trophy className="h-4 w-4 text-stone-300" />
                            <span
                                className="text-[11px] tracking-wider text-stone-300"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Top Performers
                            </span>
                        </div>
                        <SkeletonPodium />
                    </div>
                    <Separator orientation="vertical" className="bg-stone-200/60" />
                    {/* Right — skeleton rows */}
                    <div className="flex min-h-0 flex-1 flex-col px-6 pt-5 pb-4">
                        <div className="mb-2 flex items-center justify-between">
                            <span
                                className="text-[11px] tracking-wider text-stone-300"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Rankings
                            </span>
                            <span
                                className="text-[11px] tracking-wider text-stone-300"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Premium
                            </span>
                        </div>
                        <Separator className="mb-1 bg-stone-200/60" />
                        <SkeletonRows />
                    </div>
                </div>
            )}

            {/* Data loaded */}
            {hasData && (
                <div className="relative z-10 flex min-h-0 flex-1 gap-0">
                    {/* Left Panel — Podium */}
                    <div className="flex w-[42%] shrink-0 flex-col items-center px-8 pt-6">
                        {/* Section label */}
                        <div className="mb-4 flex items-center gap-2 self-start">
                            <Trophy className="h-4 w-4 text-[#456d69]" />
                            <span
                                className="text-[11px] tracking-wider text-stone-400"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Top Performers
                            </span>
                        </div>

                        {/* Podium grid — always 3 slots */}
                        <div className="grid w-full max-w-sm grid-cols-3 gap-3">
                            {/* 2nd place */}
                            <div className="mt-8 self-start">
                                <PodiumCard
                                    agent={podiumAgents[1]}
                                    position={2}
                                    isCurrentUser={podiumAgents[1] ? isCurrentUser(podiumAgents[1]) : false}
                                />
                            </div>
                            {/* 1st place — elevated */}
                            <div className="self-start">
                                <PodiumCard
                                    agent={podiumAgents[0]}
                                    position={1}
                                    isCurrentUser={podiumAgents[0] ? isCurrentUser(podiumAgents[0]) : false}
                                />
                            </div>
                            {/* 3rd place */}
                            <div className="mt-8 self-start">
                                <PodiumCard
                                    agent={podiumAgents[2]}
                                    position={3}
                                    isCurrentUser={podiumAgents[2] ? isCurrentUser(podiumAgents[2]) : false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Vertical Separator */}
                    <Separator orientation="vertical" className="bg-stone-200/60" />

                    {/* Right Panel — Rankings */}
                    <div className="flex min-h-0 flex-1 flex-col px-6 pt-5 pb-4">
                        {/* Section label */}
                        <div className="mb-2 flex items-center justify-between">
                            <span
                                className="text-[11px] tracking-wider text-stone-400"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Rankings
                            </span>
                            <span
                                className="text-[11px] tracking-wider text-stone-400"
                                style={{
                                    fontFamily: 'FredokaSemiBold, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                Premium
                            </span>
                        </div>

                        <Separator className="mb-1 bg-stone-200/60" />

                        {/* Scrollable agent rows */}
                        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                            <div className="flex flex-col gap-0">
                                {restAgents.map((agent, index) => (
                                    <AgentRow
                                        key={agent.rank}
                                        agent={agent}
                                        index={index}
                                        isCurrentUser={isCurrentUser(agent)}
                                    />
                                ))}
                            </div>

                            {restAgents.length === 0 && (
                                <div className="flex flex-1 items-center justify-center py-8">
                                    <span
                                        className="text-xs text-stone-400"
                                        style={{fontFamily: 'FredokaMedium, sans-serif'}}
                                    >
                                        No additional rankings
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Your position */}
                        {showCurrentAgent && (
                            <div className="mt-4 pt-0">
                                <div className="mb-2 flex items-center gap-2">
                                    <span
                                        className="text-[11px] tracking-wider text-stone-400"
                                        style={{
                                            fontFamily: 'FredokaSemiBold, sans-serif',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                        }}
                                    >
                                        Your Position
                                    </span>
                                    <Separator className="flex-1 bg-stone-200/60" />
                                </div>
                                <CurrentAgentRow agent={currentAgent} topAgents={agents} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Keyframe animations */}
            <style>{`
                @keyframes shimmer {
                    0%, 100% { background-position: 200% 0; }
                    50% { background-position: -200% 0; }
                }
            `}</style>
        </div>
    );
}
